'use client';

function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useCallback, useMemo } from 'react';
import { InternalProvider, useId, useDeviceDetect } from '../../util';
import { colors } from '../../abstracts';
import { makeLocale } from './languages';
import DatePicker from '@hassanmojab/react-modern-calendar-datepicker';
import { TextField } from '../TextField';
import { TextFieldHelperText } from '../TextField/elements/TextFieldHelperText';
import { DatepickerContainer, TextfieldWrapper, MobileDateInput, MobileDateInputLabel, CalendarIcon } from './elements';
import StyleContainer from './StyleContainer';
export const Datepicker = ({
  helperText = '',
  wrapperClassName = '',
  inputName = '',
  id,
  value,
  minimumDate,
  maximumDate,
  onChange,
  textFieldProps,
  language = 'en-us',
  isCustomMobile = false,
  ...restProps
}) => {
  const {
    isMobile
  } = useDeviceDetect();
  const inputId = useId('datepicker', id);
  const modifiedLocale = useMemo(() => makeLocale(language), [language]);
  const dayToLongDate = useCallback(date => date.toLocaleString(language, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }), [language]);
  const calendarDayTypeToDate = useCallback(day => new Date(day.year, day.month - 1, day.day), []);
  const dateToCalendarDayValue = useCallback(date => ({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate()
  }), []);
  const handleMobileInputChange = useCallback(event => {
    const [year, month, day] = event.target.value.split('-').map(Number);
    const localDate = new Date(year, month - 1, day);
    const utcDate = new Date(+localDate - 60000 * localDate.getTimezoneOffset());
    onChange(utcDate);
  }, []);
  const handleInputChange = useCallback(day => {
    const date = calendarDayTypeToDate(day);
    onChange(date);
  }, []);
  const renderDatePickerInput = ({
    ref
  }) => /*#__PURE__*/React.createElement(TextfieldWrapper, null, /*#__PURE__*/React.createElement(TextField, _extends({
    id: inputId,
    name: inputName || inputId
  }, textFieldProps, {
    inputClassName: "Datepicker-input",
    labelClassName: "Datepicker-label",
    ref: ref,
    readOnly: true,
    value: useMemo(() => value ? dayToLongDate(value) : '', [value]),
    affixRight: "calendar",
    affixRightClickable: true
  })));
  const dateToString = date => {
    if (!date) return '';
    return `${date.year}-${date.month < 10 ? `0${date.month}` : `${date.month}`}-${date.day}`;
  };
  const showHelperText = !!helperText;
  const renderHelperText = showHelperText && /*#__PURE__*/React.createElement(TextFieldHelperText, {
    fieldStatus: textFieldProps.fieldStatus,
    "data-testid": "helper-text"
  }, helperText);
  return /*#__PURE__*/React.createElement(InternalProvider, null, /*#__PURE__*/React.createElement(StyleContainer, null), isMobile && !isCustomMobile ? /*#__PURE__*/React.createElement(DatepickerContainer, {
    "data-aui": "datepicker"
  }, /*#__PURE__*/React.createElement(MobileDateInputLabel, {
    htmlFor: inputId
  }, textFieldProps.label), /*#__PURE__*/React.createElement(MobileDateInput, _extends({}, textFieldProps, {
    helperText: helperText,
    name: inputId,
    id: inputId,
    type: "date",
    value: value ? value.toISOString().split('T')[0] : '',
    onChange: handleMobileInputChange,
    min: dateToString(minimumDate),
    max: dateToString(maximumDate)
  })), /*#__PURE__*/React.createElement(CalendarIcon, {
    src: "calendar",
    size: "large"
  }), renderHelperText) : /*#__PURE__*/React.createElement(DatepickerContainer, {
    className: wrapperClassName,
    "data-aui": "datepicker"
  }, /*#__PURE__*/React.createElement(DatePicker, _extends({}, restProps, {
    wrapperClassName: "calendar-wrapper",
    colorPrimary: colors.black,
    value: value ? dateToCalendarDayValue(value) : null,
    onChange: handleInputChange,
    renderInput: renderDatePickerInput,
    minimumDate: minimumDate,
    maximumDate: maximumDate,
    locale: modifiedLocale
  })), renderHelperText));
};